import React, { useState, useEffect } from 'react';
import '../pages-styles/about.css';
import { usePageMeta } from '../hooks/usePageMeta';

function About() {
  usePageMeta(
    'About Magbity Agency Ltd - Professional Football Agency',
    'Discover Magbity Agency\'s mission, values, and commitment to player representation and club partnerships.'
  );

  const [aboutData, setAboutData] = useState(null);
  const [heroData, setHeroData] = useState(null);
  const [sectionsData, setSectionsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAllAboutData = async () => {
      try {
        setLoading(true);
        const apiUrl = process.env.REACT_APP_API_URL || 'https://admin.magbityagencyltd.com/api';
        console.log('Fetching from:', apiUrl);
        
        // Fetch hero data
        const heroResponse = await fetch(`${apiUrl}/about/hero/`);
        if (!heroResponse.ok) {
          throw new Error(`Failed to fetch hero: HTTP ${heroResponse.status}`);
        }
        const heroDataResult = await heroResponse.json();
        setHeroData(Array.isArray(heroDataResult) ? heroDataResult[0] : heroDataResult);
        console.log('Hero data fetched:', heroDataResult);
        
        // Fetch sections data
        const sectionsResponse = await fetch(`${apiUrl}/about/sections/`);
        if (!sectionsResponse.ok) {
          throw new Error(`Failed to fetch sections: HTTP ${sectionsResponse.status}`);
        }
        const sectionsDataResult = await sectionsResponse.json();
        setSectionsData(Array.isArray(sectionsDataResult) ? sectionsDataResult : [sectionsDataResult]);
        console.log('Sections data fetched:', sectionsDataResult);
        
        // Set combined data for backward compatibility
        setAboutData({
          why_work_with_us: sectionsDataResult?.why_work_with_us || [],
          value_to_clubs: sectionsDataResult?.value_to_clubs || {},
          mission: sectionsDataResult?.mission,
          vision: sectionsDataResult?.vision,
          core_values: sectionsDataResult?.core_values || [],
        });
        
        setError(null);
      } catch (err) {
        console.error('Error fetching about data:', err);
        setError(err.message);
        setAboutData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAllAboutData();
  }, []);

  if (loading) {
    return (
      <div className="about-page">
        <p style={{ textAlign: 'center', fontSize: '1.1rem', color: '#e0e0e0' }}>Loading about page...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="about-page">
        <div style={{ 
          background: '#ff6b6b', 
          color: 'white', 
          padding: '20px', 
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <p><strong>Error loading about page:</strong> {error}</p>
          <p style={{ fontSize: '0.9rem', marginTop: '10px' }}>Make sure the backend is running on https://admin.magbityagencyltd.com</p>
        </div>
      </div>
    );
  }

  if (!aboutData) {
    return (
      <div className="about-page">
        <p style={{ textAlign: 'center', fontSize: '1.1rem', color: '#e0e0e0' }}>No about data found.</p>
      </div>
    );
  }

  return (
    <div className="about-page">
      {/* Hero Section */}
      {heroData && heroData.image && (
        <section className="about-hero-section">
          <div className="hero-content">
            <div className="hero-image">
              <img src={heroData.image} alt={heroData.title} />
            </div>
            <div className="hero-text">
              {heroData.title && <h1>{heroData.title}</h1>}
              {heroData.subtitle && <p className="hero-subtitle">{heroData.subtitle}</p>}
            </div>
          </div>
        </section>
      )}

      {/* Sections Content */}
      {sectionsData && sectionsData.map((section, idx) => {
        // Special layout for "Our Values" section
        if (section.section_type === 'core_values' || section.title.toLowerCase().includes('value')) {
          return (
            <section key={idx} className="our-values-section">
              <div className="values-image-column">
                <img 
                  src="https://i.pinimg.com/736x/68/8a/b3/688ab3791ca9a29eac1ffbcce52576d4.jpg" 
                  alt={section.title}
                />
              </div>
              <div className="values-content-column">
                <h2>{section.title}</h2>
                {section.content && (
                  <div className="section-content">
                    {section.content}
                  </div>
                )}
              </div>
            </section>
          );
        }
        
        // Special layout for "Mission" and "Vision" sections (image on right)
        if (section.section_type === 'mission' || section.section_type === 'vision' || 
            section.title.toLowerCase().includes('mission') || section.title.toLowerCase().includes('vision')) {
          return (
            <section key={idx} className="mission-vision-section">
              <div className="mission-vision-content-column">
                <h2>{section.title}</h2>
                {section.content && (
                  <div className="section-content">
                    {section.content}
                  </div>
                )}
              </div>
              <div className="mission-vision-image-column">
                <img 
                  src="https://i.pinimg.com/736x/ec/fe/bb/ecfebb0290c7d1957d5e1ff0db6c929e.jpg" 
                  alt={section.title}
                />
              </div>
            </section>
          );
        }
        
        // Standard layout for other sections
        return (
          <section key={idx} className="about-section">
            <h2>{section.title}</h2>
            {section.content && (
              <div className="section-content">
                {section.content}
              </div>
            )}
          </section>
        );
      })}

      {/* Legacy About Data (fallback) */}
    </div>
  );
}

export default About;
